// Explicador "¿Qué es un gancho?" del modo "Video con influencer". Se abre al
// tocar el link/botón "?" debajo del selector de gancho.
//
// Mobile: bottom sheet con drag handle. Desktop: panel lateral a la derecha.
// Mismo chrome que `VideoConfigSheet`.
//
// SCAFFOLD: deja un espacio 9:16 reservado donde después irá un video vertical
// de ejemplo mostrando un gancho.

import React from 'react';
import ReactDOM from 'react-dom';
import { Film, X } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';

export interface GanchoExplainerSheetProps {
  open: boolean;
  onClose: () => void;
}

export const GanchoExplainerSheet: React.FC<GanchoExplainerSheetProps> = ({
  open,
  onClose,
}) => {
  const { mounted, animated } = useExitTransition(open, 300);
  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Mobile drag handle */}
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity md:hidden"
          >
            Cerrar
          </button>
          <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black/90 md:flex-1">
            ¿Qué es un gancho?
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <p className="text-[14px] leading-relaxed text-[#3C3C43]/75">
            Un <span className="font-semibold text-brand-black/90">gancho</span>{' '}
            es un clip corto en vertical que se coloca al inicio del video para
            captar la atención en los primeros segundos. Es lo que hace que
            alguien deje de scrollear y se quede mirando tu anuncio.
          </p>

          {/* Espacio reservado para el video vertical de ejemplo (9:16) */}
          <div className="mx-auto w-[180px] aspect-[9/16] rounded-2xl bg-neutral-100 flex flex-col items-center justify-center gap-2 text-neutral-400">
            <Film className="w-8 h-8" strokeWidth={1.5} />
            <span className="text-[11px] font-medium">Video de ejemplo</span>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};
